<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Icon } from '@iconify/vue';
import { profileDD } from '@/_mockApis/headerData';
import { useChangeProfileStore } from '@/shared/stores/changeProfileStore';
import { useDialog } from '@/shared/composables/useDialog';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { checkMenuViewPermission } from '@/shared/utils/permissionUtils';
import { shouldApplyDeptMenuVisibilityForJurisdiction } from '@/shared/utils/deptMenuVisibility';
import { useInboxStore } from '@/features/inbox/stores/inboxStore';
import { isInboxFeatureEnabled } from '@/features/inbox/config/inboxFeature';
import defaultUserImage from '@/assets/images/profile/user-1.jpg';

const sessionStore = useSessionStore();
const authStore = useAuthStore();
const inboxStore = useInboxStore();
const changeProfileStore = useChangeProfileStore();
const { warning } = useDialog();
const router = useRouter();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();

// Change Profile 메뉴 표시 여부 (IS_MULTI_CSP=1 회사만) — ProfileDD 와 요청 공유
onMounted(() => {
    void changeProfileStore.loadStatus({ refresh: true });
});

// 템플릿에서 사용할 반응형 번역 함수
const t = computed(() => createSyncT(languageStore.currentLanguage));

// 회사명 가져오기 (세션에서)
const companyName = computed(() => {
    const user = sessionStore.user;
    const isAuthenticated = sessionStore.isAuthenticated;
    const t = createSyncT(languageStore.currentLanguage);
    
    // 인증되지 않았거나 사용자 정보가 없으면 빈 문자열 반환 (표시하지 않음)
    if (!isAuthenticated || !user) {
        return '';
    }
    
    // 인증되었고 사용자 정보가 있는 경우에만 회사명 표시
    return user?.customer?.name || user?.user?.name || t('menu.user') || 'User';
});

const userEmail = computed(() => {
    const userInfo = sessionStore.getUserInfo();
    const user = sessionStore.user;
    // 백엔드에서 userEmail로 전달하는 경우도 처리
    return userInfo?.email || (userInfo as any)?.userEmail || user?.user?.email || (user?.user as any)?.userEmail || '';
});

// 프로필 이미지 URL (세션의 userImage 사용)
const userProfileImage = computed(() => {
    const userInfo = sessionStore.getUserInfo();
    const imageUrl = userInfo?.userImage;

    // 세션에 이미지가 있는 경우
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.length > 0) {
        // Data URI, 절대 URL, 상대 경로 모두 처리
        if (imageUrl.startsWith('data:')) {
            // Data URI (이미지 바이너리)
            return imageUrl;
        } else if (imageUrl.startsWith('/uploads/')) {
            // 상대 경로인 경우 절대 경로로 변환
            return `${window.location.origin}${imageUrl}`;
        } else if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            // 이미 절대 URL인 경우
            return imageUrl;
        }
    }
    // 기본 이미지 (세션에 이미지가 없거나 비정상인 경우)
    return defaultUserImage;
});

// 다국어 처리 + 전 관할 view 권한 기반 가시성 필터 적용된 profileDD
const translatedProfileDD = computed(() => {
    // computed 내부에서 번역 함수 생성 (언어 변경 시 자동 업데이트)
    const t = createSyncT(languageStore.currentLanguage);

    // 1) 타이틀 번역
    const inboxEnabled = isInboxFeatureEnabled();

    const mapped = profileDD
        .filter((item) => inboxEnabled || item.href !== '/pages/inbox')
        .map(item => {
        let translatedTitle = item.title;
        
        // title을 다국어 키로 매핑
        const titleMap: Record<string, string> = {
            'My Subscription': t('menu.mySubscription') || 'My Subscription',
            'Account Settings': t('menu.accountSettings') || 'Account Settings',
            'Billing Settings': t('menu.Billing Settings') || 'Billing Settings',
            'Authorization Settings': t('menu.authorizationSettings') || 'Authorization Settings',
            'Users': t('menu.Users') || 'Users',
            'Inbox': t('menu.inbox') || 'Inbox',
            'Sign Out': t('menu.signOut') || 'Sign Out'
        };
        
        if (titleMap[item.title]) {
            translatedTitle = titleMap[item.title];
        }
        
        return {
            ...item,
            title: translatedTitle
        };
    });

    // Change Profile — Users 다음(없으면 Sign Out 앞, 그것도 없으면 맨 끝). href 없는 항목이라 권한 필터를 통과함
    if (changeProfileStore.enabled) {
        const usersIdx = mapped.findIndex(i => i.href === '/users');
        const logoutIdx = mapped.findIndex(i => i.href === '/auth/logout');
        const at = usersIdx >= 0 ? usersIdx + 1 : (logoutIdx >= 0 ? logoutIdx : mapped.length);
        mapped.splice(at, 0, { title: t('menu.changeProfile') || 'Change Profile', href: '', badge: false, action: 'changeProfile' });
    }

    // 2) 세션·관할 확인 후 view 필터 적용 (전 관할, MY/비-MY 공통)
    const customerInfo = sessionStore.user?.customer || null;
    const jurisdiction = String(customerInfo?.jurisdiction || '').toUpperCase();
    const shouldApplyDeptMenuVisibility = shouldApplyDeptMenuVisibilityForJurisdiction(jurisdiction);
    if (!shouldApplyDeptMenuVisibility) return mapped;

    // 3) view 권한 기반 필터 (Account Settings는 항상)
    const perms = sessionStore.getPermissions as any;
    const accountTitle = t('menu.accountSettings') || 'Account Settings';
    const authzTitle = t('menu.authorizationSettings') || 'Authorization Settings';

    const inboxTitle = t('menu.inbox') || 'Inbox';

    return mapped.filter(item => {
        // logout은 항상 표시
        if (!item.href || item.href === '/auth/logout') return true;
        // Account Settings는 항상 표시
        if (item.title === accountTitle) return true;
        // Inbox는 로그인 사용자 전원 표시
        if (item.href === '/pages/inbox' || item.title === inboxTitle) return true;
        // Authorization Settings는 메뉴 권한(view)으로만 표시 결정
        if (item.title === authzTitle) {
            return checkMenuViewPermission(perms, '/pages/authorization-settings');
        }
        // 나머지는 view 권한으로 필터
        return checkMenuViewPermission(perms, item.href);
    });
});

// 메뉴 클릭 처리
const handleMenuClick = async (item: any) => {
    if (item.action === 'changeProfile') {
        changeProfileStore.open();
        return;
    }
    const t = createSyncT(languageStore.currentLanguage);
    // CSP 프로필로 전환한 상태에서는 원래 회사 전용 메뉴로 이동하지 않고 안내(C69)
    if (changeProfileStore.isOwnerOnlyBlocked(item.href)) {
        await warning(
            t('menu.changeProfile') || 'Change Profile',
            (t('menu.changeProfileOwnerOnly') || 'This menu is only available from {companyName}. Switch back to {companyName} in Change Profile.')
                .replace(/\{companyName\}/g, changeProfileStore.ownerName || t('menu.changeProfileOwner') || 'Your company')
        );
        return;
    }
    const signOutText = t('menu.signOut') || 'Sign Out';
    if (item.title === 'Sign Out' || item.title === signOutText) {
        // 로그아웃 처리
        await authStore.logout();
        // 로그인 페이지로 리다이렉트
        router.push('/auth/login');
    }
};
</script>

<template>
    <v-menu open-on-click>
        <template v-slot:activator="{ props }">
            <div v-if="companyName" class="d-flex justify-space-between align-center w-100 cursor-pointer" v-bind="props">
                <h5 class="text-white font-weight-medium text-15">{{ companyName }}</h5>
                <Icon icon="solar:alt-arrow-down-bold" class="text-white" height="15" />
            </div>
            <div v-else class="d-flex justify-space-between align-center w-100">
                <h5 class="text-white font-weight-medium text-15">{{ t('menu.user') || 'User' }}</h5>
            </div>
        </template>

        <v-sheet rounded="md" width="230" elevation="10">
            <div class="pb-4">
                <div class="d-flex align-center pa-4">
                    <div class="avatar-wrapper">
                        <img 
                            :src="userProfileImage" 
                            width="50" 
                            height="50"
                            :alt="companyName"
                            class="avatar-image"
                        />
                    </div>
                    <div class="ml-3">
                        <h6 class="text-h6 textPrimary font-weight-medium">{{ companyName }}</h6>
                        <span class="text-12 textSecondary">{{ userEmail }}</span>
                    </div>
                </div>
                <v-divider></v-divider>
                <perfect-scrollbar style="height: calc(100vh - 240px); max-height: 240px">
                    <v-list class="py-0 px-4 theme-list mt-3" lines="one">
                        <v-list-item
                            v-for="item in translatedProfileDD"
                            :key="item.title"
                            class="py-0 px-2 rounded-md custom-text-primary"
                            color="primary"
                            :to="item.href && item.href !== '/auth/logout' && !changeProfileStore.isOwnerOnlyBlocked(item.href) ? item.href : undefined"
                            @click="item.href === '/auth/logout' || item.action || changeProfileStore.isOwnerOnlyBlocked(item.href) ? handleMenuClick(item) : undefined"
                        >
                            <div class="d-flex gap-3 align-center justify-space-between w-100">
                                <p class="text-subtitle-1 textPrimary hover-primary font-weight-medium mb-0">
                                    {{ item.title }}
                                </p>
                                <v-chip
                                    v-if="item.href === '/pages/inbox' && inboxStore.unreadCount > 0"
                                    size="x-small"
                                    color="error"
                                    variant="flat"
                                    label
                                >
                                    {{ t('common.inbox.newBadge') || 'NEW' }}
                                </v-chip>
                                <v-chip v-else-if="item.badge" size="small" color="info">4</v-chip>
                            </div>
                        </v-list-item>
                    </v-list>
                </perfect-scrollbar>
            </div>
        </v-sheet>
    </v-menu>
</template>

<style scoped>
.avatar-wrapper {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(var(--v-theme-surface), 0.1);
    flex-shrink: 0;
}

.avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
}
</style>
